import { PropsWithChildren, useCallback } from "react"
import {
  CheckboxOffIcon,
  CheckboxOnFiledIcon,
  getColorFromTheme,
  RadioOffIcon,
  RadioOnIcon,
  theme,
  ThemeColors,
  Typography,
} from ".."
import styled, { keyframes } from "styled-components"

const clickFeedback = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(0,0,0,0);
  }
  50% {
    transform: scale(0.98);
    box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.08);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(0,0,0,0);
  }
`

const Card = styled.div<{ $isSelected: boolean; $hasBorder?: boolean; $highlightColor: string; disabled?: boolean }>`
  display: flex;
  padding: 1rem;
  flex-direction: row;
  align-items: flex-start;
  border-radius: 0.5rem;
  border: 1px solid
    ${({ $isSelected, $hasBorder, $highlightColor, theme }) =>
      $isSelected ? $highlightColor : $hasBorder ? theme.colors.grey.$200 : "transparent"};
  cursor: pointer;
  gap: 0.5rem;

  background-color: ${({ disabled }) => (disabled ? theme.colors.grey.$100 : "transparent")};

  transition:
    border-color 0.3s ease,
    background-color 0.3s ease;

  &.animating {
    animation: ${clickFeedback} 200ms ease-out;
  }
`

const BoxContainer = styled.div`
  display: flex;
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Box = ({
  isSelected,
  type,
  $highlightColor,
  disabled,
}: {
  isSelected: boolean
  type: CheckboxType
  $highlightColor: string
  disabled?: boolean
}) => {
  const EmptyBox = type === "radio" ? RadioOffIcon : CheckboxOffIcon
  const FilledBox = type === "radio" ? RadioOnIcon : CheckboxOnFiledIcon

  return (
    <BoxContainer>
      {isSelected ? (
        <FilledBox
          height="24px"
          width="24px"
          stroke={disabled ? theme.colors.grey.$300 : $highlightColor}
          color={disabled ? theme.colors.grey.$300 : $highlightColor}
        />
      ) : (
        <EmptyBox
          height="24px"
          width="24px"
          color={theme.colors.grey.$300}
          fill={disabled ? theme.colors.grey.$300 : "transparent"}
        />
      )}
    </BoxContainer>
  )
}

type CheckboxType = "checkbox" | "radio"

export const Checkbox = ({
  isSelected,
  onClick,
  type = "checkbox",
  text,
  children,
  disabled,
  $hasBorder,
  $highlightColor = "secondary.$200",
}: PropsWithChildren<{
  isSelected: boolean
  onClick: () => void
  text: string
  type?: CheckboxType
  disabled?: boolean
  $hasBorder?: boolean
  $highlightColor?: ThemeColors
}>) => {
  const color = getColorFromTheme($highlightColor, theme.colors)
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      e.stopPropagation()
      e.preventDefault()
      if (!disabled) {
        onClick()
      }
    },
    [disabled, onClick],
  )
  return (
    <Card
      $isSelected={isSelected}
      $hasBorder={$hasBorder}
      onClick={handleClick}
      onKeyDown={handleClick}
      tabIndex={0}
      $highlightColor={color}
      disabled={disabled}
    >
      <Box isSelected={isSelected} type={type} $highlightColor={color} disabled={disabled} />
      <TextContainer>
        <Typography $variant="body-4-regular">{text}</Typography>
        {children}
      </TextContainer>
    </Card>
  )
}
